import {
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ComponentPortal, Portal } from '@angular/cdk/portal';
import { WebSpeechApiService } from '../library/web-speech-api.service';
import { ControlService } from '../library/control.service';
import { AppConstants } from 'src/app/app-constants';
import { UserVideoService } from '../library/user-video.service';
import { DeviceSettingsService } from '../library/device-settings.service';
import { DeviceSettingsSpeechRecognitionComponent } from '../device-settings/device-settings-speech-recognition/device-settings-speech-recognition.component';
import { Subscription } from 'rxjs';
import { PlayerService } from '../library/player.service';
import { AmplitudeService } from 'src/app/services/external-tools/amplitude.service';
import { AmplitudeTrackingEvents } from 'src/app/utils/enums/amplitude/amplitudeTrackingEvents.enum';
import { AmplitudeTrackingEventPropValues } from 'src/app/utils/enums/amplitude/amplitudeTrackingEventPropValues.enum';
import { BreakpointObserver } from '@angular/cdk/layout';
import { ICallVisualAid } from 'src/app/interfaces/icall-visual-aid';
import { CallStoreService } from '../library/call-store/call-store.service';
import { LoggerService } from 'src/app/services/logger/logger.service';
interface NetworkNavigator extends Navigator {
  connection?: {
    effectiveType: 'slow-2g' | '2g' | '3g' | '4g' | 'lte';
  };
}
@Component({
  selector: 'app-call-visual-aid',
  standalone: false,
  templateUrl: './call-visual-aid.component.html',
  styleUrls: ['./call-visual-aid.component.scss'],
})
export class CallVisualAidComponent implements OnInit, OnDestroy {
  @Input() webSpeechApiService: WebSpeechApiService | undefined;
  @Input() controlService: ControlService | undefined;
  @Input() userVideoService: UserVideoService | undefined;
  @Input() deviceSettingsService: DeviceSettingsService | undefined;
  @Input() playerService: PlayerService | undefined;
  @Input() practiceCallUuid: string = '';
  advanceTypeManual: string = AppConstants.ADVANCE_TYPE_MANUAL;
  advanceTypeAuto: string = AppConstants.ADVANCE_TYPE_AUTOMATIC;
  errorSubscription!: Subscription;
  selectedPortal: Portal<any> | null = null;
  componentPortal: ComponentPortal<DeviceSettingsSpeechRecognitionComponent> | null =
    null;
  userName: string = localStorage.getItem('user_first_name') || 'User';
  timeoutResize: any;
  visualAidDisabled: boolean = true;
  isPortrait: boolean = true;
  imgArray: string[] = [];
  preloadedImages: HTMLImageElement[] = [];
  imageExtensions: string[] = ['.jpeg', '.jpg', '.png', '.gif'];
  currentPage: number = 0;
  visualAid: ICallVisualAid | undefined;
  displayErrorBanner: boolean = false;
  private loadedImageIndices: Set<number> = new Set();
  enableVisualAid: boolean = false;

  constructor(
    private amplitudeService: AmplitudeService,
    private breakpointObserver: BreakpointObserver,
    private cdr: ChangeDetectorRef,
    public callStore: CallStoreService,
    private logger: LoggerService
  ) {}

  async ngOnInit(): Promise<void> {
    if (this.controlService) {
      this.callStore.setDisplayPostCall(false);
      this.visualAid = this.callStore.getCall()?.visualAid;
      this.preloadImages();
      const display = this.controlService.shouldDisplayTour();
      if (display) {
        this.controlService.displayTour = true;
      }
    }
    // Subscribing to error message, waiting for the error that can emit this in the webSpeechApiService logic
    if (this.webSpeechApiService) {
      this.errorSubscription = this.webSpeechApiService
        .getError()
        .subscribe(() => {
          if (this.componentPortal === null) {
            this.componentPortal = new ComponentPortal(
              DeviceSettingsSpeechRecognitionComponent
            );
          }
          this.selectedPortal = this.componentPortal;
        });
    }

    window.addEventListener('online', () => {
      console.log('Network reconnected, retrying missing images...');
      if (this.visualAid) {
        const missingIndices = this.getMissingImageIndices(
          Number(this.visualAid.fileCount)
        );
        this.preloadMissingImages(missingIndices);
      }
    });

    this.breakpointObserver
      .observe(['(orientation: portrait)', '(orientation: landscape)'])
      .subscribe((result) => {
        this.isPortrait = result.breakpoints['(orientation: portrait)'];
        this.handleUserVideoOnResize();
        this.cdr.detectChanges();
      });
  }

  ngOnDestroy(): void {
    if (this.errorSubscription) {
      this.errorSubscription.unsubscribe();
    }
    // remove speech recognition validation interval
    if (this.controlService) {
      this.playerService?.clearTranscripts();
      if (this.controlService.intervalSpeechDetected) {
        clearInterval(this.controlService.intervalSpeechDetected);
      }
    }
  }

  ngAfterViewInit(): void {
    this.preloadImages();
    if (this.controlService) {
      this.controlService.pauseAfterInactivity();
    }

    if (this.userVideoService) {
      this.userVideoService.displayUserVideo();
    }

    if (this.deviceSettingsService) {
      this.deviceSettingsService.startAudioLevelDetection();
    }
  }

  videoSize(): void {
    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;

    const widthCoefficient = screenHeight > screenWidth ? 0.65 : 0.5;
    const heightCoefficient = screenHeight > screenWidth ? 0.3 : 0.45;

    // Calculate the width and height
    const resultWidth = Math.round(screenWidth * widthCoefficient);
    const resultHeight = Math.round(screenHeight * heightCoefficient);

    const avatarVideo = document.getElementById('avatar-video');
    if (avatarVideo) {
      avatarVideo.style.width = `${resultWidth}px`;
      avatarVideo.style.height = `${resultHeight}px`;
    }

    const videos = document.getElementById('videos');
    if (videos) {
      videos.style.height = `${resultHeight}px`;
    }
  }

  // on resize of the window, adjust the height of the user-container
  onResize(event: any): void {
    if (this.playerService) {
      this.playerService.avatarOffsetHeight = 0;
      this.playerService.avatarOffsetWidth = 0;
      this.playerService?.getVideoHeight();
    }
    this.handleUserVideoOnResize();
  }

  handleUserVideoOnResize(): void {
    clearTimeout(this.timeoutResize);
    const isCamera = !this.deviceSettingsService?.isCameraUnavailable;
    const isVideoOn = this.controlService?.isVideoOn;
    if (isVideoOn && isCamera) {
      this.timeoutResize = setTimeout(() => {
        const video = document.getElementById('user-video') as HTMLVideoElement;
        if (video?.paused && this.userVideoService?.userVideoStream) {
          try {
            video.srcObject = this.userVideoService.userVideoStream;
            video.play().catch((error) => {
              console.error('Error playing user camera video:', error);
            });
          } catch (error) {
            throw new Error('Error playing the user video on visual aid');
          }
        }
      }, 200);
    }
  }

  copyToClipboard(): void {
    const uuid = this.callStore.getCall()?.practiceCallUuid || '';
    navigator.clipboard.writeText(uuid);
  }

  startCallAfterTour(): void {
    if (this.controlService) {
      this.controlService.displayTourHint = false;
      this.controlService.displayTour = false;
      this.controlService.unpauseCall();
    }
  }

  public sendAmplitudeClickPauseEvent(): void {
    // Sending the Event of Clicking on Pause to Amplitude
    this.amplitudeService.trackEvent(AmplitudeTrackingEvents.CALL_CLICK_PAUSE, {
      call_type: this.controlService?.callType,
    });
  }

  public sendAmplitudeClickMuteEvent(): void {
    // Sending the Event of Clicking on Mute to Amplitude
    this.amplitudeService.trackEvent(AmplitudeTrackingEvents.CALL_CLICK_MUTE, {
      mute_status: this.callStore.getIsMicOn()
        ? AmplitudeTrackingEventPropValues.MUTE_STATUS_UNMUTED
        : AmplitudeTrackingEventPropValues.MUTE_STATUS_MUTED,
      call_type: this.controlService?.callType,
    });
  }

  public sendAmplitudeClickCameraEvent(): void {
    // Sending the Event of Clicking on Start/Stop Video to Amplitude
    this.amplitudeService.trackEvent(
      AmplitudeTrackingEvents.CALL_CLICK_CAMERA,
      {
        camera_status: this.controlService?.isVideoOn
          ? AmplitudeTrackingEventPropValues.CAMERA_STATUS_CAMERA_ON
          : AmplitudeTrackingEventPropValues.CAMERA_STATUS_CAMERA_OFF,
        call_type: this.controlService?.callType,
      }
    );
  }

  public sendAmplitudeClickEndCallEvent(): void {
    // Sending the Event of Clicking on End Call to Amplitude
    this.amplitudeService.trackEvent(
      AmplitudeTrackingEvents.CALL_CLICK_ENDCALL,
      {
        call_type: this.controlService?.callType,
      }
    );
  }

  // Click Handlers

  public handlePauseClick(): void {
    this.sendAmplitudeClickPauseEvent();
    this.controlService?.togglePause();
  }

  public handleMuteClick(): void {
    this.sendAmplitudeClickMuteEvent();
    this.controlService?.toggleMic();
  }

  public handleCameraClick(): void {
    this.sendAmplitudeClickCameraEvent();
    this.controlService?.toggleVideo();
  }

  public async handleEndCallClick(): Promise<void> {
    this.sendAmplitudeClickEndCallEvent();
    if (this.playerService) {
      this.playerService.handleSendUserResponseVisualAid(
        'page-' + (this.visualAidDisabled ? 0 : this.currentPage + 1) + ' | '
      );
      await new Promise((resolve) => setTimeout(resolve, 1000));
    }
    this.controlService?.endCallVisualAid();
  }

  preloadImages(): void {
    this.checkConnectivity();
    if (navigator.onLine) {
      if (
        !this.visualAid ||
        !this.visualAid?.fileCount ||
        !this.visualAid?.baseUrl
      ) {
        console.error('Visual aid or file count or base URL is not defined.');
        this.logVisualAidError(
          'Visual aid or file count or baseurl is not defined.'
        );
        return;
      }

      for (let i = 0; i < Number(this.visualAid.fileCount); i++) {
        const baseSrc = `${this.visualAid.baseUrl}${i + 1}`;
        this.loadImageWithExtensions(baseSrc, i + 1);
      }
    }
  }

  loadImageWithExtensions(baseSrc: string, index: number): void {
    let attempts = 0;
    const tryNextExtension = (): void => {
      if (attempts < this.imageExtensions.length) {
        const src = baseSrc + this.imageExtensions[attempts];
        attempts++;
        this.loadImage(src, tryNextExtension, index);
      } else {
        console.error(`Image ${index} failed to load with any extension.`);
        this.logVisualAidError(
          `Image ${index} failed to load with any extension.`
        );
      }
    };

    tryNextExtension();
  }

  loadImage(src: string, onError: () => void, index: number): void {
    console.log('loadedImageIndices', this.loadedImageIndices);
    if (this.loadedImageIndices.has(index)) {
      console.warn(`Skipping duplicate load for Image ${index}`);
      return;
    }
    const img = new Image();
    img.src = src;

    img.onload = () => {
      if (!this.loadedImageIndices.has(index)) {
        this.imgArray.push(src);
        this.preloadedImages.push(img);
        this.loadedImageIndices.add(index);
        const extensionMatch = src.match(/\.(jpeg|jpg|png|gif)$/);
        const extension = extensionMatch ? extensionMatch[1] : 'jpeg';
        console.log('selected extension:', extension);
        this.imgArray = this.sortImagesByNumber(this.imgArray, extension);
        console.log(this.imgArray.length + ' image(s) loaded');
        console.log(
          `Image ${index} loaded. Total: ${this.preloadedImages.length} images.`
        );
      }
    };

    img.onerror = () => {
      console.error(`Failed to load Image ${index}: ${src}`);
      this.logVisualAidError(`Failed to load Image ${index}: ${src}`);
      onError();
    };
  }

  sortImagesByNumber(imgArray: string[], extension: string): string[] {
    return imgArray.sort((a, b) => {
      const extractNumber = (url: string): number => {
        const regex = new RegExp(`(\\d+)(?=\\.${extension})`);
        const match = url.match(regex);
        return match ? parseInt(match[0], 10) : 0;
      };
      const numA = extractNumber(a);
      const numB = extractNumber(b);

      return numA - numB;
    });
  }

  toggleVisualAd() {
    this.playerService?.handleSendUserResponseVisualAid(
      'page-' + (this.visualAidDisabled ? 0 : this.currentPage + 1) + ' | '
    );
    this.addVisualAidAmplitudeTrackingEvent();

    if (
      !this.visualAid ||
      !this.visualAid?.fileCount ||
      !this.visualAid?.baseUrl
    ) {
      this.logVisualAidError(
        'Visual aid or file count or baseurl is not defined.'
      );
      this.showErrorBanner();
      return;
    }

    const totalImages = Number(this.visualAid.fileCount);
    const missingIndices = this.getMissingImageIndices(totalImages);
    if (missingIndices.length > 0) {
      console.warn(
        `Missing ${missingIndices.length} images. Displaying error banner.`
      );
      this.showErrorBanner();
      this.preloadMissingImages(missingIndices);
      return;
    }

    if (this.displayErrorBanner) {
      console.log('All images loaded, hiding error banner.');
      this.hideErrorBanner();
    }

    this.visualAidDisabled = !this.visualAidDisabled;
    this.handleUserVideoOnResize();
  }

  getMissingImageIndices(totalImages: number): number[] {
    const expectedIndices = new Set(
      [...Array(totalImages).keys()].map((i) => i + 1)
    );
    console.log('expected indices', expectedIndices);
    const loadedIndices = new Set(this.loadedImageIndices);
    console.log('loadedIndices', loadedIndices);
    return [...expectedIndices].filter((index) => !loadedIndices.has(index));
  }

  preloadMissingImages(missingIndices: number[]): void {
    this.checkConnectivity();
    if (navigator.onLine) {
      missingIndices.forEach((index) => {
        const baseSrc = `${this.visualAid?.baseUrl}${index}`;
        this.loadImageWithExtensions(baseSrc, index);
      });
    }
  }

  checkConnectivity() {
    const networkNavigator = navigator as NetworkNavigator;
    if (networkNavigator.connection?.effectiveType) {
      console.log(
        `Network speed detected: ${networkNavigator.connection.effectiveType}`
      );
      if (
        ['slow-2g', '2g', '3g', 'slow-4g'].includes(
          networkNavigator.connection.effectiveType
        )
      ) {
        console.warn('Slow network detected');
        this.logVisualAidError('Visual aid loading - slow network detected.');
      }
    }
    if (!navigator.onLine) {
      console.log('Visual aid failed to load due to no internet connection.');
      this.logVisualAidError(
        'Visual aid failed to load due to no internet connection.'
      );
      window.addEventListener('online', () => this.preloadImages(), {
        once: true,
      });
      return;
    }
  }

  showErrorBanner() {
    this.displayErrorBanner = true;
    this.cdr.detectChanges();
  }

  hideErrorBanner() {
    this.displayErrorBanner = false;
    this.cdr.detectChanges();
  }
  next() {
    if (
      this.currentPage + 1 === this.imgArray.length ||
      this.imgArray.length === 0
    )
      return;
    this.playerService?.handleSendUserResponseVisualAid(
      'page-' + (this.currentPage + 1) + ' | '
    );
    this.currentPage += 1;
  }

  previous() {
    if (this.currentPage === 0) return;
    this.playerService?.handleSendUserResponseVisualAid(
      'page-' + (this.currentPage + 1) + ' | '
    );
    this.currentPage -= 1;
  }
  addVisualAidAmplitudeTrackingEvent(): void {
    this.visualAidDisabled
      ? this.amplitudeService.trackEvent(
          AmplitudeTrackingEvents.CALL_CLICK_DISPLAY_VISUAL_AID,
          {
            call_type: this.controlService?.callType,
          }
        )
      : this.amplitudeService.trackEvent(
          AmplitudeTrackingEvents.CALL_CLICK_CLOSE_VISUAL_AID,
          {
            call_type: this.controlService?.callType,
          }
        );
  }

  handleContinue() {
    this.playerService?.handleSendUserResponseVisualAid(
      'page-' + (this.visualAidDisabled ? 0 : this.currentPage + 1) + ' | '
    );
    this.callStore.setIsContinueButtonBlocked(true);
    this.callStore.setIsSendingAnswer(true);
    setTimeout(() => {
      this.playerService?.nextClipVisualAid();
    }, 2500);
  }

  logVisualAidError(errorMessage: string) {
    const userUuid = localStorage.getItem('user_uuid');
    const visualAidId = this.visualAid?.uuid || '';
    const browserInfo = navigator.userAgent;
    const ua = navigator.userAgent.toLowerCase();
    const isIpadOld = ua.includes('ipad');
    const isIpadNew = /macintosh/.test(ua) && 'ontouchend' in document;
    const deviceType =
      isIpadNew || isIpadOld
        ? 'iPad'
        : /Mobi|Android|iPhone/i.test(ua)
        ? 'Mobile'
        : 'Desktop';

    const sentryTagsPayload = {
      browser_info: browserInfo,
      device_type: deviceType,
      user_uuid: userUuid ?? '',
      visual_aid_id: visualAidId,
    };

    this.logger.error('[CALL] visual-aid error', new Error(errorMessage), undefined, sentryTagsPayload);
  }

  avatarVideoIsReady(item: any) {
    console.error('AVATAR::Ready', item);
    this.enableVisualAid = true;
  }

  avatarLoadingError(event: Event, item: any): void {
    const videoElement = event.target as HTMLVideoElement;
    const error = videoElement.error;

    if (error) {
      switch (error.code) {
        case MediaError.MEDIA_ERR_ABORTED:
          console.error('Video load aborted by user or browser:', item.src);
          break;
        case MediaError.MEDIA_ERR_NETWORK:
          console.error('Network error while loading video:', item.src);
          break;
        case MediaError.MEDIA_ERR_DECODE:
          console.error('Decoding error while playing video:', item.src);
          break;
        case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
          console.error('Video format or source not supported:', item.src);
          break;
        default:
          console.error('Unknown video error occurred:', item.src);
      }
    } else {
      console.error('An unknown error occurred while loading video:', item.src);
    }
  }
}
